import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UploadCloud, AlertTriangle, RotateCw, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import AccountingReviewDrawer from "@/components/AccountingReviewDrawer";
import APInvoicesView from "@/components/APInvoicesView";
import VendorCreditsView from "@/components/VendorCreditsView";
import AccountingDocumentsView from "@/components/AccountingDocumentsView";
import { ExportPanel } from "@/pages/contractor/AccountingQueue";
import { useAuth } from "@/contexts/AuthContext";
import { CARD_IMPORT_ROLES } from "@/constants/roles";
import { fmtDate } from "@/lib/utils";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const TYPE_LABELS = { invoice: "Vendor invoice", outside_purchase: "Expense", card_transaction: "Card", purchase_order: "PO" };

// Meeting 3 — Invoices and the Review Queue are one page, in two steps:
// ① approve (vendor invoices here; Expense Log entries are approved by
// their approver under Procurement) → ② sync to QuickBooks. Paying an
// open bill and the history sit alongside.
export default function Accounting() {
  const { user } = useAuth();
  const isAccounting = CARD_IMPORT_ROLES.includes(user?.role);
  return isAccounting ? <AccountingWorklist /> : <InvoicesToApprove />;
}

function Header({ subtitle }) {
  return (
    <>
      <h1 className="text-2xl font-extrabold tracking-tight">Accounting</h1>
      <p className="text-slate-500 text-sm mt-1">{subtitle}</p>
    </>
  );
}

function TabButton({ active, onClick, children, testId, tone = "slate" }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-3.5 py-2 text-[12.5px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${active ? (tone === "amber" ? "border-amber-500 text-amber-700" : "border-sky-500 text-sky-600") : "border-transparent text-slate-500 hover:text-slate-700"}`}>
      {children}
    </button>
  );
}

function AccountingWorklist() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "to_approve";
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const q = useDebounced(search);
  const [jobs, setJobs] = useState([]);
  const [isQboMode, setIsQboMode] = useState(true);
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(null); // item id | "all"
  const navigate = useNavigate();

  // Each stage is its own server-side page, searched across every record.
  const isListTab = ["to_approve", "to_sync", "done"].includes(tab);
  const list = usePagedSearch("/contractor/accounting/worklist", { stage: isListTab ? tab : "to_approve", q });
  const load = list.reload;
  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/settings").then((r) => setIsQboMode((r.data.accounting_mode || "export_only") !== "export_only"));
  }, []);

  if (!list.counts) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const jobCode = (id) => jobs.find((j) => j.id === id)?.code || "—";
  const counts = list.counts;
  const items = isListTab ? (list.items || []) : [];
  const syncLabel = isQboMode ? "To sync" : "To export";

  const open = (i) => {
    if (i.type === "invoice" && i.stage === "to_approve" && i.invoice_status) navigate(`/contractor/invoices/${i.id}`);
    else setSelected(i);
  };

  const syncOne = async (i) => {
    setSyncing(i.id);
    try {
      const { data: r } = await api.post(`/contractor/accounting/${i.type}/${i.id}/sync`);
      if (r.ok) toast.success(`${i.label} synced to QuickBooks`);
      else toast.error(r.error || "Sync failed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  const syncAll = async () => {
    setSyncing("all");
    try {
      const { data: r } = await api.post("/contractor/accounting/sync-all");
      if (r.failed > 0) toast.info(`Synced ${r.synced}, ${r.failed} failed — they're marked below`);
      else toast.success(`Synced ${r.synced} to QuickBooks`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  const stageTabs = [
    { key: "to_approve", label: "To approve", step: "1" },
    { key: "to_sync", label: syncLabel, step: "2" },
    { key: "to_pay", label: "To pay" },
    { key: "done", label: "Completed" },
  ];

  return (
    <div className="p-6 md:p-8" data-testid="accounting-page">
      <Header subtitle={isQboMode
        ? "Two steps: approve each vendor invoice, then sync to QuickBooks. Expense Log entries arrive already approved."
        : "Two steps: approve each vendor invoice, then export. Expense Log entries arrive already approved."} />

      <div className="flex gap-1 mt-5 border-b border-slate-100 overflow-x-auto items-end">
        {stageTabs.map((t) => (
          <TabButton key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} testId={`accounting-tab-${t.key}`}>
            {t.step && <span className="mr-1.5 inline-flex w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] items-center justify-center align-middle">{t.step}</span>}
            {t.label}{counts[t.key] > 0 && t.key !== "done" ? ` (${counts[t.key]})` : ""}
          </TabButton>
        ))}
        <span className="flex-1" />
        <TabButton active={tab === "documents"} onClick={() => setTab("documents")} testId="accounting-tab-documents">Documents</TabButton>
        <TabButton active={tab === "credits"} onClick={() => setTab("credits")} testId="accounting-tab-credits">Credits</TabButton>
        <TabButton active={tab === "export"} onClick={() => setTab("export")} testId="accounting-tab-export">Export</TabButton>
      </div>

      {isListTab && (
        <ListSearchBar value={search} onChange={setSearch} placeholder="Search invoice #, PO #, vendor, merchant, expense #…" testId="accounting-search" />
      )}

      {tab === "to_pay" && <APInvoicesView initialTab="open" />}
      {tab === "credits" && <VendorCreditsView prefillInvoiceId={searchParams.get("credit_for")} />}
      {tab === "documents" && <AccountingDocumentsView />}
      {tab === "export" && <ExportPanel isQboMode={isQboMode} />}

      {["to_approve", "to_sync", "done"].includes(tab) && (
        <>
          {tab === "to_sync" && isQboMode && items.length > 0 && (
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[12.5px] text-slate-500">
                Approved and ready. Expense Log entries and invoices wait for you to sync them; everything else also syncs on the auto-sync schedule.
                {counts.sync_errors > 0 && <span className="text-red-600 font-semibold"> {counts.sync_errors} failed — fix and retry below.</span>}
              </p>
              <button onClick={syncAll} disabled={!!syncing} data-testid="sync-all-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2CA01C] hover:bg-[#248217] text-white text-[13px] font-semibold disabled:opacity-60">
                <UploadCloud size={14} /> {syncing === "all" ? "Syncing…" : `Sync all (${(counts.to_sync || 0).toLocaleString()})`}
              </button>
            </div>
          )}
          {tab === "to_sync" && !isQboMode && items.length > 0 && (
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[12.5px] text-slate-500">Approved and ready to export.</p>
              <button onClick={() => setTab("export")} className="px-4 py-2.5 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Export these</button>
            </div>
          )}
          {tab === "to_approve" && (
            <p className="mt-4 text-[12.5px] text-slate-500">Open a vendor invoice to check each line's cost code, then approve it. Approved means ready for QuickBooks.</p>
          )}

          <div className="sm:hidden mt-4 space-y-2">
            {items.map((i) => (
              <div key={`${i.type}-${i.id}`} onClick={() => open(i)} className="border border-slate-200 rounded-xl p-3.5 active:bg-slate-50" data-testid={`work-card-${i.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="mono text-sky-600 font-semibold text-[13px] truncate">{i.label}</span>
                  <span className="mono font-bold text-[13px]">{fmt(i.amount)}</span>
                </div>
                <div className="text-[12.5px] text-slate-600 truncate">{TYPE_LABELS[i.type]} · {i.vendor_name || "—"}</div>
                {i.sync_error && <div className="text-[12px] text-red-700 mt-1">{i.sync_error.message}</div>}
                {tab === "to_sync" && isQboMode && (
                  <button onClick={(e) => { e.stopPropagation(); syncOne(i); }} disabled={!!syncing} className="mt-2 w-full h-10 rounded-lg border border-slate-200 text-[12.5px] font-semibold">
                    {syncing === i.id ? "Syncing…" : i.sync_error ? "Retry sync" : "Sync"}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="hidden sm:block overflow-x-auto mt-4">
            <table className="w-full text-[13px]" data-testid={`accounting-table-${tab}`}>
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                  <th className="text-left py-2">Number</th>
                  <th className="text-left py-2">Type</th>
                  <th className="text-left py-2">Vendor</th>
                  <th className="text-left py-2">Job</th>
                  <th className="text-left py-2">Date</th>
                  <th className="text-right py-2">Amount</th>
                  <th className="text-left py-2 pl-3">{tab === "to_sync" ? "Approved by" : "Status"}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={`${i.type}-${i.id}`} onClick={() => open(i)} className={`cursor-pointer hover:bg-slate-50 ${i.sync_error ? "bg-red-50/40" : ""}`} data-testid={`work-row-${i.label.replace(/[^a-zA-Z0-9-]/g, "_")}`}>
                    <td className="py-2.5 border-t border-slate-100 mono text-sky-600 font-medium">{i.label}</td>
                    <td className="py-2.5 border-t border-slate-100 text-slate-600">
                      {TYPE_LABELS[i.type]}
                      {i.match_status === "discrepancy" && <span className="ss-pill ss-pill-amber ml-1">Match ⚠</span>}
                    </td>
                    <td className="py-2.5 border-t border-slate-100">{i.vendor_name || "—"}</td>
                    <td className="py-2.5 border-t border-slate-100 text-slate-500">{i.job_id ? jobCode(i.job_id) : "—"}</td>
                    <td className="py-2.5 border-t border-slate-100 text-slate-500 whitespace-nowrap">{i.date ? fmtDate(i.date) : "—"}</td>
                    <td className="py-2.5 border-t border-slate-100 text-right mono font-medium">{fmt(i.amount)}</td>
                    <td className="py-2.5 border-t border-slate-100 pl-3 text-[12.5px]">
                      {tab === "to_sync" ? (
                        i.sync_error
                          ? <span className="text-red-700 flex items-center gap-1"><AlertTriangle size={12} /> {i.sync_error.message}</span>
                          : <span className="text-slate-500">{i.approved_by_name || "—"}</span>
                      ) : tab === "to_approve" ? (
                        i.accounting_status === "needs_info"
                          ? <StatusBadge status="needs_info" />
                          : <StatusBadge status={i.invoice_status === "received" ? "in_review" : i.invoice_status === "sent" ? "new" : "pending_review"} />
                      ) : (
                        <StatusBadge status={i.accounting_status === "synced" && !isQboMode ? "exported" : i.accounting_status} />
                      )}
                    </td>
                    <td className="py-2.5 border-t border-slate-100 text-right whitespace-nowrap">
                      {tab === "to_sync" && isQboMode ? (
                        <button onClick={(e) => { e.stopPropagation(); syncOne(i); }} disabled={!!syncing} data-testid={`sync-row-${i.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold hover:bg-slate-50 disabled:opacity-60">
                          {i.sync_error ? <RotateCw size={12} /> : <UploadCloud size={12} />} {syncing === i.id ? "Syncing…" : i.sync_error ? "Retry" : "Sync"}
                        </button>
                      ) : (
                        <ChevronRight size={14} className="text-slate-300 inline" />
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">
                    {tab === "to_approve" ? "Nothing waiting for approval." : tab === "to_sync" ? `Nothing ${isQboMode ? "to sync" : "to export"}.` : "Nothing here yet."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <LoadMore items={list.items} nextCursor={list.nextCursor} loadMore={list.loadMore} loadingMore={list.loadingMore} total={counts[tab]} />
        </>
      )}

      <AccountingReviewDrawer
        item={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        onChanged={load}
        isQboMode={isQboMode}
        startEditing={!!selected?.sync_error}
      />
    </div>
  );
}

// Purchasers and PMs: just the vendor invoices they need to code and
// approve — no sync or payment status (Meeting 3).
function InvoicesToApprove() {
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("to_approve");
  const [search, setSearch] = useState("");
  const q = useDebounced(search);
  const navigate = useNavigate();

  // Searched on the server (invoice #, PO #, vendor).
  useEffect(() => { api.get("/contractor/invoices", { params: q ? { search: q } : {} }).then((r) => setInvoices(r.data)); }, [q]);
  const rows = tab === "to_approve" ? invoices.filter((i) => ["sent", "received"].includes(i.status)) : invoices;
  const shown = (s) => (["sent", "received"].includes(s) ? (s === "sent" ? "new" : "in_review") : "approved");

  return (
    <div className="p-6 md:p-8" data-testid="accounting-page">
      <Header subtitle="Vendor invoices against your purchase orders — check each line's cost code and approve." />
      <ListSearchBar value={search} onChange={setSearch} placeholder="Search invoice #, PO #, vendor…" testId="invoice-search" />
      <div className="flex gap-1 mt-5 border-b border-slate-100">
        <TabButton active={tab === "to_approve"} onClick={() => setTab("to_approve")} testId="invoices-tab-to-approve">
          To approve{invoices.filter((i) => ["sent", "received"].includes(i.status)).length > 0 ? ` (${invoices.filter((i) => ["sent", "received"].includes(i.status)).length})` : ""}
        </TabButton>
        <TabButton active={tab === "all"} onClick={() => setTab("all")} testId="invoices-tab-all">All invoices</TabButton>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full mt-4 text-[13px]" data-testid="contractor-invoices-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Invoice #</th><th className="text-left py-2.5">Vendor</th><th className="text-left py-2.5">PO #</th>
              <th className="text-left py-2.5">Date</th><th className="text-right py-2.5">Total</th><th className="text-left py-2.5 pl-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((inv) => (
              <tr key={inv.id} onClick={() => navigate(`/contractor/invoices/${inv.id}`)} className="cursor-pointer hover:bg-slate-50 border-b border-slate-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                <td className="py-3 mono text-sky-600 font-medium">{inv.invoice_number}</td>
                <td className="py-3 text-slate-600">{inv.vendor_name || "—"}</td>
                <td className="py-3 mono text-slate-600">{inv.po_number || "—"}</td>
                <td className="py-3 text-slate-600">{fmtDate(inv.invoice_date)}</td>
                <td className="py-3 text-right mono font-medium">{fmt(inv.total)}</td>
                <td className="py-3 pl-3"><StatusBadge status={shown(inv.status)} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400 text-sm">{tab === "to_approve" ? "Nothing waiting for approval." : "No invoices yet."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
